import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  LayoutDashboard,
  Bookmark,
  Ticket,
  LogOut,
  Pencil,
  Check,
  X,
  ChevronRight,
  Mail,
  MapPin,
  CalendarDays,
  Shield,
  Star,
  Compass,
  Sparkles,
  Upload,
  Gift,
  Lock,
  Eye,
  EyeOff,
  CreditCard,
  Settings,
  Settings2,
  Search,
  Bell,
  CircleHelp,
  Headset,
  Heart,
  Menu,
  BadgeCheck,
  Percent,
  Clock,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { Utensils } from "lucide-react";
import { GiftIcon } from "@/components/icons/CustomIcons";

import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";

import { useFavorites } from "@/hooks/useFavorites";
import { selectSavedItems } from "@/lib/favorites";
import { type Item } from "@/data/places";
import { usePublicPlaces } from "@/hooks/use-public-places";
import { compressImage as compressImageShared, LEGACY_OUTPUT_TYPES, MAX_IMAGE_BYTES } from "@/lib/compress-image";
import { getMyProfile, updateMyProfile, uploadMyAvatar } from "@/lib/profile.functions";
import { listMyTicketOrders } from "@/lib/tickets.functions";
import { getMyBookings, cancelMyBooking } from "@/lib/bookings.functions";
import { TicketModal } from "@/components/TicketModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PlaceCard } from "@/components/PlaceCard";
import { MembershipCard } from "@/components/MembershipCard";
import { SafeAvatar } from "@/components/SafeAvatar";
import { NotificationsBell } from "@/components/NotificationsBell";



const VALID_TABS = ["overview", "saved", "membership", "bookings", "settings"] as const;

export const Route = createFileRoute("/profile")({
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput) => {
    const raw = typeof search.tab === "string" ? search.tab : undefined;
    const tab = raw && (VALID_TABS as readonly string[]).includes(raw) ? (raw as (typeof VALID_TABS)[number]) : undefined;
    return { tab };
  },
  head: () => ({
    meta: [
      { title: "Your profile — what Finland" },
      { name: "description", content: "Manage your saved places, bookings, and membership on what Finland." },
      { property: "og:title", content: "Your profile — what Finland" },
      { property: "og:description", content: "Manage your saved places, bookings, and membership on what Finland." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ProfilePage,
});

type TabKey = "overview" | "saved" | "membership" | "bookings" | "settings";

type NavIcon = (props: { className?: string; size?: number; strokeWidth?: number }) => React.ReactNode;

const NAV: { key: TabKey; label: string; icon: NavIcon }[] = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "saved", label: "Saved", icon: Heart },
  { key: "membership", label: "Membership", icon: CreditCard },
  { key: "bookings", label: "Booking", icon: CalendarDays },
  { key: "settings", label: "Settings", icon: Settings2 },
];

function initialsFrom(name: string | null | undefined, email: string | null | undefined) {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+|@|\./).filter(Boolean);
  const letters = (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
  return letters.toUpperCase().slice(0, 2) || "?";
}

function OrdersList({
  orders,
  loading,
  mode,
}: {
  orders: any[];
  loading: boolean;
  mode: "bookings" | "tickets";
}) {
  const [activeTicket, setActiveTicket] = useState<any | null>(null);

  if (loading) {
    return <div className="py-8 text-center text-sm text-slate-500">loading…</div>;
  }

  if (!orders || orders.length === 0) {
    return (
      <Empty
        text={mode === "bookings" ? "no bookings yet" : "no tickets yet"}
        hint={
          mode === "bookings"
            ? "when you buy a ticket on /tickets, your booking confirmation will appear here."
            : "your purchased tickets and QR codes will appear here."
        }
      />
    );
  }

  return (
    <>
      <div className="space-y-4">
        {orders.map((o) => (
          <div key={o.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-start justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  order · {new Date(o.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                </div>
                <div className="text-sm font-semibold text-slate-900 mt-0.5">
                  {o.tickets?.length ?? 0} {o.tickets?.length === 1 ? "ticket" : "tickets"} · {o.buyer_name}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-slate-900">
                  {new Intl.NumberFormat("en-IE", { style: "currency", currency: o.currency || "EUR" }).format((o.total_cents ?? 0) / 100)}
                </div>
                <span className="inline-block mt-0.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                  {o.status}
                </span>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {(o.tickets ?? []).map((t: any) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTicket(t)}
                  className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                >
                  {t.image_url ? (
                    <img src={t.image_url} alt="" className="size-12 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="size-12 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 grid place-items-center text-white shrink-0">
                      <Ticket className="size-5" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{t.product_name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {t.holder_name} · {new Date(t.valid_date).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} · #{t.shortId}
                    </div>
                  </div>
                  {t.used_at ? (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-500">used</span>
                  ) : (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">valid</span>
                  )}
                  <ChevronRight className="size-4 text-slate-300 group-hover:text-slate-600 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <TicketModal
        ticket={activeTicket}
        open={!!activeTicket}
        onOpenChange={(v: boolean) => { if (!v) setActiveTicket(null); }}
      />
    </>
  );
}

function BookingsList({
  bookings,
  loading,
  onCancel,
}: {
  bookings: any[];
  loading: boolean;
  onCancel: (id: string) => void;
}) {
  if (loading) return <div className="py-8 text-center text-sm text-slate-500">loading…</div>;
  if (!bookings || bookings.length === 0) {
    return (
      <Empty
        text="no reservations yet"
        hint="book a table at a restaurant or a tour from any place page — your reservations will appear here."
      />
    );
  }
  return (
    <div className="space-y-3">
      {bookings.map((b) => {
        const statusTone =
          b.status === "confirmed"
            ? "bg-emerald-50 text-emerald-700"
            : b.status === "cancelled"
              ? "bg-slate-100 text-slate-500"
              : "bg-amber-50 text-amber-700";
        const payTone =
          b.payment_status === "paid" || b.payment_status === "free"
            ? "bg-emerald-50 text-emerald-700"
            : b.payment_status === "pending"
              ? "bg-amber-50 text-amber-700"
              : "bg-slate-100 text-slate-500";
        const payLabel =
          b.payment_status === "paid"
            ? "paid"
            : b.payment_status === "free"
              ? "free"
              : b.payment_status === "pending"
                ? b.payment_method === "bank_transfer"
                  ? "awaiting transfer"
                  : "awaiting payment"
                : "unpaid";
        const amount =
          b.price_cents > 0
            ? new Intl.NumberFormat("en-GB", {
                style: "currency",
                currency: (b.currency || "EUR").toUpperCase(),
              }).format(b.price_cents / 100)
            : null;
        const KindIcon = b.kind === "restaurant" ? Utensils : Compass;
        return (
          <div key={b.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <Link
              to="/place/$id"
              params={{ id: b.place_slug }}
              className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors group"
            >
              {b.place_image_url ? (
                <img src={b.place_image_url} alt="" className="size-14 rounded-lg object-cover shrink-0" />
              ) : (
                <div className="size-14 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 grid place-items-center text-white shrink-0">
                  <KindIcon className="size-5" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                    {b.kind === "restaurant" ? "restaurant" : "tour"}
                  </span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusTone}`}>
                    {b.status}
                  </span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${payTone}`}>
                    {payLabel}
                  </span>
                  {amount && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                      {amount}
                    </span>
                  )}
                </div>
                <div className="text-sm font-semibold text-slate-900 truncate mt-0.5">{b.place_name}</div>
                <div className="text-xs text-slate-500 truncate">
                  {new Date(b.booking_date).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
                  {b.booking_time ? ` · ${String(b.booking_time).slice(0, 5)}` : ""} · {b.party_size} {b.party_size === 1 ? "guest" : "guests"}
                </div>
                {b.payment_status === "pending" && b.stripe_checkout_url && (
                  <a
                    href={b.stripe_checkout_url}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-block mt-1 text-[11px] font-semibold text-sky-700 hover:underline"
                  >
                    complete payment →
                  </a>
                )}
                {b.payment_status === "pending" && b.payment_method === "bank_transfer" && b.bank_reference && (
                  <div className="mt-1 text-[11px] text-amber-700">
                    transfer ref: <span className="font-mono">{b.bank_reference}</span>
                  </div>
                )}
              </div>
              {b.status !== "cancelled" && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (confirm("cancel this reservation?")) onCancel(b.id);
                  }}
                  className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1.5 rounded-full text-rose-700 hover:bg-rose-50 border border-rose-200"
                >
                  cancel
                </button>
              )}
              <ChevronRight className="size-4 text-slate-300 group-hover:text-slate-600 transition-colors" />
            </Link>
          </div>
        );
      })}
    </div>
  );
}


function ProfilePage() {
  const { user, session, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { favs } = useFavorites();
  const ITEMS = usePublicPlaces();
  const savedItems = useMemo(() => selectSavedItems(favs, ITEMS), [favs, ITEMS]);

  const search = Route.useSearch();
  const [tab, setTab] = useState<TabKey>((search.tab as TabKey) ?? "overview");

  useEffect(() => {
    if (search.tab) setTab(search.tab as TabKey);
  }, [search.tab]);



  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
  }, [loading, user, navigate]);

  const fetchProfile = useServerFn(getMyProfile);
  const saveProfile = useServerFn(updateMyProfile);
  const uploadAvatar = useServerFn(uploadMyAvatar);

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      try {
        return await fetchProfile();
      } catch {
        // Auth race / stale session — return null instead of crashing the page.
        return null;
      }
    },
    enabled: !loading && !!user && !!session?.access_token,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const fetchMyOrders = useServerFn(listMyTicketOrders);
  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ["my-ticket-orders", user?.id],
    queryFn: async () => {
      try { return await fetchMyOrders(); } catch { return { orders: [] }; }
    },
    enabled: !loading && !!user && !!session?.access_token,
  });
  const myOrders = (ordersData?.orders ?? []) as any[];
  const totalTicketCount = useMemo(
    () => myOrders.reduce((acc, o) => acc + (o.tickets?.length ?? 0), 0),
    [myOrders]
  );

  const fetchMyBookings = useServerFn(getMyBookings);
  const cancelBookingFn = useServerFn(cancelMyBooking);
  const { data: bookingsData, isLoading: bookingsLoading, refetch: refetchBookings } = useQuery({
    queryKey: ["my-bookings", user?.id],
    queryFn: async () => {
      try { return await fetchMyBookings(); } catch { return { bookings: [] }; }
    },
    enabled: !loading && !!user && !!session?.access_token,
  });
  const myBookings = (bookingsData?.bookings ?? []) as any[];

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ display_name: "", home_city: "", bio: "", avatar_url: "" });
  const [avatarPreview, setAvatarPreview] = useState<string>("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [locating, setLocating] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  async function detectLocation() {
    if (!("geolocation" in navigator)) { toast.error("Geolocation not supported"); return; }
    setLocating(true);
    try {
      const pos = await new Promise<GeolocationPosition>((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 10000 })
      );
      const { latitude, longitude } = pos.coords;
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&accept-language=en`);
      const j = await r.json();
      const city = j.address?.city || j.address?.town || j.address?.village || j.address?.municipality || j.address?.county || "";
      if (!city) { toast.error("Could not detect city"); return; }
      setForm((f) => ({ ...f, home_city: String(city).toLowerCase() }));
      toast.success(`Detected: ${city}`);
    } catch (e: any) {
      toast.error(e?.message ?? "Location denied");
    } finally {
      setLocating(false);
    }
  }

  /** Validate the bytes are a real image, resize to 1024px and squeeze under 100 KB. */
  async function compressImage(input: File, maxBytes = MAX_IMAGE_BYTES): Promise<File> {
    return compressImageShared(input, maxBytes, 1024, {
      outputTypes: LEGACY_OUTPUT_TYPES,
      maxInputBytes: 15 * 1024 * 1024,
    });
  }

  async function handleAvatarUpload(file: File, opts?: { autoSave?: boolean }) {
    if (!user) return;
    setUploadingAvatar(true);
    try {
      const compressed = await compressImage(file);
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(new Error("Failed to read image"));
        reader.readAsDataURL(compressed);
      });

      const callUpload = () =>
        uploadAvatar({
          data: {
            fileName: compressed.name,
            mimeType: compressed.type as "image/jpeg" | "image/webp",
            dataUrl,
          },
        });

      let uploaded;
      try {
        uploaded = await callUpload();
      } catch (err: any) {
        const msg = String(err?.message ?? err ?? "");
        // Stale / malformed access token — refresh and retry once.
        if (/Invalid Compact JWS|Invalid token|Unauthorized/i.test(msg)) {
          const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError || !refreshed.session) {
            await supabase.auth.signOut();
            toast.error("Session expired — please sign in again.");
            return;
          }
          uploaded = await callUpload();
        } else {
          throw err;
        }
      }

      // Save the storage path; display the freshly-signed URL.
      setForm((f) => ({ ...f, avatar_url: uploaded.path }));
      const previewUrl = uploaded.signedUrl ?? uploaded.path;
      setAvatarPreview(previewUrl);
      const kb = Math.round(compressed.size / 1024);
      if (opts?.autoSave) {
        await saveProfile({ data: { avatar_url: uploaded.path } });
        queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
        toast.success(`Photo updated (${kb} KB)`);
      } else {
        toast.success(`Photo uploaded (${kb} KB)`);
      }

    } catch (e: any) {
      toast.error(e?.message ?? "Upload failed");
    } finally {
      setUploadingAvatar(false);
    }
  }

  useEffect(() => {
    if (profile && !editing) {
      setForm({
        display_name: profile.display_name ?? "",
        home_city: profile.home_city ?? "",
        bio: profile.bio ?? "",
        avatar_url: "",
      });
      setAvatarPreview(profile.avatar_url ?? "");
    }
  }, [profile, editing]);

  const AVATAR_REMOVE = "__remove__";

  const mutation = useMutation({
    mutationFn: (vars: typeof form) => {
      const payload: Record<string, unknown> = {
        display_name: vars.display_name.trim() || null,
        home_city: vars.home_city.trim() || null,
        bio: vars.bio.trim() || null,
      };
      const v = vars.avatar_url.trim();
      if (v === AVATAR_REMOVE) payload.avatar_url = null;
      else if (v) payload.avatar_url = v;
      return saveProfile({ data: payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
      setEditing(false);
    },
  });

  const displayName = profile?.display_name || user?.email?.split("@")[0] || "you";
  const initials = initialsFrom(profile?.display_name, user?.email);
  const joinedAt = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long" })
    : null;

  // profile completeness — must run before any early return (rules of hooks)
  const completion = useMemo(() => {
    const fields = [profile?.display_name, profile?.home_city, profile?.bio, profile?.avatar_url, user?.email];
    const filled = fields.filter((f) => !!f && String(f).trim() !== "").length;
    return Math.round((filled / fields.length) * 100);
  }, [profile, user?.email]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500 bg-slate-50">
        loading…
      </div>
    );
  }

  const autoTier = savedItems.length >= 10 ? "explorer" : savedItems.length >= 3 ? "rookie" : "newcomer";
  const memberTier = ((profile as any)?.membership_tier as string | null) || autoTier;


  const activeNav = NAV.find((n) => n.key === tab)!;
  const counts = {
    saved: savedItems.length,
    bookings: myBookings.length,
  };
  const tierLabel = memberTier.replace(/_/g, " ");

  return (
    <div className="h-dvh overflow-hidden flex bg-[#eef1f6] text-slate-900 not-italic" style={{ fontFamily: '"Inter Variable", Inter, ui-sans-serif, system-ui, sans-serif' }}>
      {sidebarOpen ? (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <aside
        className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static inset-y-0 left-0 z-50 w-[240px] shrink-0 flex flex-col text-white transition-transform duration-150`}
        style={{ background: "#0b1d3a" }}
      >
        <div className="p-3">
          <div className="rounded-2xl bg-white/[0.07] ring-1 ring-white/10 p-3">
            <div className="flex items-center gap-2">
              <Link to="/" aria-label="Home" className="flex min-w-0 items-center gap-[0.4em] leading-none not-italic">
                <span className="text-[15px] font-semibold tracking-tight text-white">what</span>
                <span className="text-white/35 font-semibold">·</span>
                <span className="text-[15px] font-semibold tracking-tight text-white">member</span>
              </Link>
              <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">v2</span>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <SafeAvatar
                userId={user.id}
                size={96}
                eager
                src={profile?.avatar_url}
                name={displayName}
                email={user.email}
                className="size-10 rounded-xl object-cover"
                fallbackClassName="size-10 rounded-xl grid place-items-center bg-white/10 text-white text-sm font-bold"
                roundedClassName="rounded-xl"
              >
                {initials}
              </SafeAvatar>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold truncate not-italic">{displayName}</p>
                <p className="text-[10px] uppercase tracking-wider text-white/55 truncate">
                  <span className="inline-block size-1.5 rounded-full bg-emerald-400 mr-1.5 align-middle" />
                  Online · {tierLabel}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-white/45 mb-1">
                <span>Profile</span>
                <span>{completion}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#2ec4b6] transition-all" style={{ width: `${completion}%` }} />
              </div>
            </div>
          </div>
        </div>

        <div className="px-3 flex-1 overflow-y-auto">
          <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35">Main</div>
          <nav className="space-y-1">
            {NAV.map((item) => {
              const Icon = item.icon;
              const active = tab === item.key;
              const count = counts[item.key as keyof typeof counts];
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => { setTab(item.key); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-2.5 py-[9px] rounded-full text-[13.5px] font-medium text-left transition-colors"
                  style={{
                    background: active ? "rgba(255,255,255,0.10)" : "transparent",
                    color: active ? "#ffffff" : "rgba(255,255,255,0.72)",
                  }}
                >
                  <span className="size-8 rounded-full grid place-items-center shrink-0 bg-white/[0.08]">
                    <Icon className="size-4" strokeWidth={1.75} />
                  </span>
                  <span className="flex-1 truncate">{item.label}</span>
                  {typeof count === "number" && count > 0 && (
                    <span className="text-[10px] font-bold min-w-[20px] h-5 px-1.5 rounded-full grid place-items-center bg-[#3b82f6] text-white">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="px-3 pt-6 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35">Support</div>
          <Link
            to="/contact"
            onClick={() => setSidebarOpen(false)}
            className="w-full flex items-center gap-3 px-2.5 py-[9px] rounded-full text-[13.5px] font-medium text-white/70 hover:text-white hover:bg-white/10"
          >
            <span className="size-8 rounded-full grid place-items-center shrink-0 bg-white/[0.08]">
              <Headset className="size-4" strokeWidth={1.75} />
            </span>
            Support & Contact
          </Link>

          <div className="px-3 pt-6 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35">Account</div>
          <button
            type="button"
            onClick={async () => { await signOut(); navigate({ to: "/" }); }}
            className="w-full flex items-center gap-3 px-2.5 py-[9px] rounded-full text-[13.5px] font-medium text-rose-300 hover:text-rose-200 hover:bg-white/10"
          >
            <span className="size-8 rounded-full grid place-items-center shrink-0 bg-white/[0.08]">
              <LogOut className="size-4" strokeWidth={1.75} />
            </span>
            Sign out
          </button>
        </div>

      </aside>

      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
         <header className="relative z-20 shrink-0 h-14 bg-white/80 backdrop-blur border-b border-slate-200/80 flex items-center gap-3 px-3 sm:px-5">
          <button
            type="button"
            className="md:hidden rounded-lg p-2 text-slate-600 hover:bg-slate-100"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="size-5" />
          </button>
          <div className="flex items-center gap-1.5 text-[13px] text-slate-400 min-w-0">
            <span className="hidden sm:inline">pages</span>
            <span className="hidden sm:inline">›</span>
            <span className="hidden sm:inline">account</span>
            <span className="hidden sm:inline">›</span>
            <span className="font-semibold text-slate-800 truncate">{activeNav.label}</span>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#0b1d3a] text-white text-[13px] font-medium hover:bg-[#0b1d3a]/90 transition-colors"
            >
              <ArrowLeft className="size-3.5" />
              <span className="hidden sm:inline">Back to site</span>
            </Link>
            <label className="hidden md:flex items-center gap-2 h-9 w-56 rounded-full bg-slate-100 px-3 text-slate-400">
              <Search className="size-3.5 shrink-0" />
              <input
                type="search"
                placeholder="search dashboard…"
                className="bg-transparent text-[13px] text-slate-700 placeholder:text-slate-400 outline-none w-full"
              />
            </label>
            <Link to="/help" className="size-9 grid place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Help">
              <CircleHelp className="size-4" />
            </Link>
            {user?.id ? (
              <NotificationsBell userId={user.id} />
            ) : (
              <span className="size-9 grid place-items-center rounded-full text-slate-300" aria-hidden="true">
                <Bell className="size-4" />
              </span>
            )}
          </div>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-5 md:p-6">
          <div className="max-w-6xl mx-auto space-y-4 sm:space-y-5">
              {tab === "overview" && (
                <HeroBanner
                  displayName={displayName}
                  initials={initials}
                  avatar={profile?.avatar_url}
                  email={user.email ?? ""}
                  city={profile?.home_city}
                  bio={profile?.bio}
                  joinedAt={joinedAt}
                  completion={completion}
                  tier={memberTier}
                  onEdit={() => setTab("settings")}
                  onAvatarSelect={(f) => handleAvatarUpload(f, { autoSave: true })}
                  uploading={uploadingAvatar}
                />
              )}

              {tab !== "overview" && tab !== "membership" && (
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight not-italic">{activeNav.label}</h1>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {tab === "saved" && "Places you've starred across Finland"}
                      {tab === "bookings" && "Your tickets, passes and orders"}
                      {tab === "settings" && "Profile details and preferences"}
                    </p>
                  </div>
                  {tab === "settings" && !editing && (
                    <Button type="button" onClick={() => setEditing(true)} className="gap-1.5 bg-[#0b1d3a] hover:bg-[#0b1d3a]/90">
                      <Pencil className="size-3.5" /> Edit Profile
                    </Button>
                  )}
                </div>
              )}

              {tab === "overview" && (
                <>
                  <OverviewTab
                    user={user}
                    profile={profile}
                    savedCount={savedItems.length}
                    ticketCount={totalTicketCount}
                    bookingCount={myBookings.length}
                    savedItems={savedItems}
                    onJump={(k) => setTab(k)}
                  />
                  <MembershipCard
                    displayName={displayName}
                    email={user.email ?? ""}
                    memberId={user.id.replace(/-/g, "")}
                    memberNumber={(profile as any)?.member_number ?? null}
                    joinDate={profile?.created_at ?? null}
                    tier={memberTier}
                    joinedAt={joinedAt}
                    avatarUrl={profile?.avatar_url ?? null}
                  />
                </>
              )}

              {tab === "saved" && (
                <Card title="saved places" right={<span className="text-xs text-slate-500">{savedItems.length} items</span>}>
                  {savedItems.length === 0 ? (
                    <Empty
                      text="nothing saved yet"
                      action={<Link to="/eat" className="inline-block bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-md">find places</Link>}
                    />
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-7">
                      {savedItems.map((it) => <PlaceCard key={it.id} item={it} />)}
                    </div>
                  )}
                </Card>
              )}

              {tab === "membership" && (
                <div className="space-y-4 sm:space-y-5">
                  <MembershipCard
                    displayName={displayName}
                    email={user.email ?? ""}
                    memberId={user.id.replace(/-/g, "")}
                    memberNumber={(profile as any)?.member_number ?? null}
                    joinDate={profile?.created_at ?? null}
                    tier={memberTier}
                    joinedAt={joinedAt}
                    avatarUrl={profile?.avatar_url ?? null}
                  />

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                      { icon: BadgeCheck, tint: "#d4a017", label: "Tier", value: memberTier },
                      { icon: Bookmark, tint: "#e11d48", label: "Saved", value: String(savedItems.length) },
                      { icon: CalendarDays, tint: "#0d6efd", label: "Member since", value: joinedAt ?? "—" },
                      { icon: Shield, tint: "#198754", label: "Status", value: "Active" },
                    ].map((k) => {
                      const Icon = k.icon;
                      return (
                      <div key={k.label} className="rounded-2xl bg-white border border-slate-200 shadow-sm px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          <span className="size-8 rounded-xl grid place-items-center shrink-0" style={{ background: `${k.tint}18`, color: k.tint }}>
                            <Icon className="size-4" strokeWidth={1.85} />
                          </span>
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{k.label}</span>
                        </div>
                        <div className="mt-2 text-lg font-semibold text-slate-900 capitalize truncate not-italic">{k.value}</div>
                      </div>
                      );
                    })}
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5">
                      <h2 className="text-sm font-semibold text-slate-900 mb-4">How it works</h2>
                      <ol className="space-y-3">
                        {[
                          { n: "1", icon: CreditCard, title: "Open your card", body: "Keep Your what Card ready on this page." },
                          { n: "2", icon: BadgeCheck, title: "Show it at checkout", body: "Present the active card to staff when ordering." },
                          { n: "3", icon: Percent, title: "Discount applied", body: "They add the member rate on their POS — no scan needed." },
                        ].map((s) => {
                          const StepIcon = s.icon;
                          return (
                          <li key={s.n} className="flex gap-3">
                            <span className="size-8 rounded-full bg-[#0b1d3a] text-white text-[12px] font-semibold grid place-items-center shrink-0">{s.n}</span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                                <StepIcon className="size-3.5 text-slate-400" strokeWidth={1.85} />
                                {s.title}
                              </div>
                              <p className="text-[13px] text-slate-500 mt-0.5 leading-relaxed">{s.body}</p>
                            </div>
                          </li>
                          );
                        })}
                      </ol>
                    </div>

                    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 flex flex-col">
                      <h2 className="text-sm font-semibold text-slate-900 mb-1">Next perk</h2>
                      <p className="text-[13px] text-slate-500 mb-4">Keep exploring to unlock the next tier.</p>
                      <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
                        <div className="flex items-center gap-2 text-amber-800">
                          <Sparkles className="size-4" />
                          <span className="text-sm font-semibold">
                            {memberTier === "newcomer" ? "Save 3 places → Rookie" : memberTier === "rookie" ? "Save 10 places → Explorer" : "You're at the top"}
                          </span>
                        </div>
                        <p className="text-[12px] text-amber-800/70 mt-1">
                          {memberTier === "newcomer" || memberTier === "rookie"
                            ? `${savedItems.length} saved so far.`
                            : "Enjoy founder and explorer benefits."}
                        </p>
                      </div>
                      <p className="mt-auto pt-4 text-[12px] text-slate-500">
                        Use <span className="font-semibold text-slate-700">Download</span> on the card to save a PNG to your device.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <h2 className="text-sm font-semibold text-slate-900">Member perks</h2>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 bg-slate-100 rounded-full px-2.5 py-1">
                        <Clock className="size-3" /> Coming soon
                      </span>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      {[
                        { icon: Gift, title: "Partner restaurants", body: "Member rates at selected kitchens across Finland." },
                        { icon: Sparkles, title: "Hotels & saunas", body: "Welcome drinks and upgrades at participating partners." },
                        { icon: Ticket, title: "Early event access", body: "First look at tickets for selected happenings." },
                      ].map((perk) => {
                        const PerkIcon = perk.icon;
                        return (
                        <div key={perk.title} className="relative rounded-xl border border-slate-100 bg-slate-50/70 p-4">
                          <div className="size-9 rounded-lg bg-white text-[#0b1d3a] grid place-items-center mb-3 ring-1 ring-slate-200">
                            <PerkIcon className="size-4" strokeWidth={1.85} />
                          </div>
                          <h3 className="text-sm font-semibold text-slate-900">{perk.title}</h3>
                          <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">{perk.body}</p>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {tab === "bookings" && (
                <BookingsTab
                  myBookings={myBookings}
                  bookingsLoading={bookingsLoading}
                  totalTicketCount={totalTicketCount}
                  myOrders={myOrders}
                  ordersLoading={ordersLoading}
                  onCancel={async (id) => {
                    try {
                      await cancelBookingFn({ data: { id } });
                      await refetchBookings();
                    } catch (e: any) {
                      console.error(e);
                    }
                  }}
                />
              )}






              {tab === "settings" && (
                <>
                <Card title="profile details">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="display name">
                      {editing ? (
                        <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} maxLength={80} />
                      ) : (
                        <p className="text-sm text-slate-900">{profile?.display_name || "—"}</p>
                      )}
                    </Field>
                    <Field label="home city">
                      {editing ? (
                        <div className="flex gap-2">
                          <Input value={form.home_city} onChange={(e) => setForm({ ...form, home_city: e.target.value })} maxLength={80} placeholder="helsinki" />
                          <button type="button" onClick={detectLocation} disabled={locating}
                            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-medium text-slate-700 disabled:opacity-50">
                            <MapPin className="size-3.5" />
                            {locating ? "locating…" : "use my location"}
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-900">{profile?.home_city || "—"}</p>
                      )}
                    </Field>
                    <Field label="email">
                      <p className="text-sm text-slate-900">{user.email}</p>
                    </Field>
                    <Field label="member since">
                      <p className="text-sm text-slate-900">{joinedAt || "—"}</p>
                    </Field>
                    <Field label="photo" wide>
                      {editing ? (
                        <div className="flex items-center gap-3">
                          {avatarPreview ? (
                            <SafeAvatar
                              src={avatarPreview}
                              name={displayName}
                              email={user.email}
                              className="size-16 rounded-xl object-cover ring-1 ring-slate-200"
                              fallbackClassName="size-16 rounded-xl bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center text-[10px] text-slate-400"
                              roundedClassName="rounded-xl"
                            >
                              no photo
                            </SafeAvatar>
                          ) : (
                            <div className="size-16 rounded-xl bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center text-[10px] text-slate-400">no photo</div>
                          )}
                          <div className="flex-1 flex flex-wrap gap-2">
                            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium cursor-pointer hover:bg-slate-800">
                              <Upload className="size-4" />
                              {uploadingAvatar ? "uploading…" : (avatarPreview ? "replace" : "upload photo")}
                              <input type="file" accept="image/*" className="hidden" disabled={uploadingAvatar}
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAvatarUpload(f); e.currentTarget.value = ""; }} />
                            </label>
                            {avatarPreview && (
                              <button type="button" onClick={() => { setForm({ ...form, avatar_url: AVATAR_REMOVE }); setAvatarPreview(""); }} className="px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100">remove</button>
                            )}
                          </div>
                        </div>
                      ) : (
                        profile?.avatar_url ? (
                          <SafeAvatar
                            userId={user.id}
                            size={160}
                            src={profile.avatar_url}
                            name={displayName}
                            email={user.email}
                            className="size-16 rounded-xl object-cover ring-1 ring-slate-200"
                            fallbackClassName="size-16 rounded-xl bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center text-[10px] text-slate-400"
                            roundedClassName="rounded-xl"
                          >
                            no photo
                          </SafeAvatar>
                        ) : (
                          <p className="text-sm text-slate-900">—</p>
                        )
                      )}
                    </Field>
                    <Field label="bio" wide>
                      {editing ? (
                        <>
                          <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} maxLength={280} rows={3} />
                          <p className="text-[10px] text-slate-400 text-right mt-1">{form.bio.length}/280</p>
                        </>
                      ) : (
                        <p className="text-sm text-slate-700">{profile?.bio || "—"}</p>
                      )}
                    </Field>
                  </div>

                  {editing && (
                    <>
                      {mutation.error && (
                        <p className="mt-4 text-sm text-rose-600">
                          {mutation.error instanceof Error ? mutation.error.message : "could not save"}
                        </p>
                      )}
                      <div className="mt-6 flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
                        <Button type="button" variant="ghost" onClick={() => setEditing(false)} disabled={mutation.isPending}>
                          <X className="size-4" /> cancel
                        </Button>
                        <Button type="button" onClick={() => mutation.mutate(form)} disabled={mutation.isPending} className="gap-1.5 bg-sky-600 hover:bg-sky-700">
                          <Check className="size-4" /> {mutation.isPending ? "saving…" : "save changes"}
                        </Button>
                      </div>
                    </>
                  )}

                  {!editing && (
                    <div className="mt-8 pt-6 border-t border-slate-100">
                      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-3">account actions</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={async () => { await signOut(); navigate({ to: "/" }); }}
                        className="gap-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      >
                        <LogOut className="size-4" /> log out
                      </Button>
                    </div>
                  )}
                </Card>
                <div className="mt-4"><ChangePasswordCard email={user.email ?? ""} /></div>
                </>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ Hero Banner ============ */
function HeroBanner({
  displayName, initials, avatar, email, city, bio, joinedAt, completion, tier, onEdit, onAvatarSelect, uploading,
}: {
  displayName: string; initials: string; avatar?: string | null; email: string;
  city?: string | null; bio?: string | null; joinedAt: string | null;
  completion: number; tier: string; onEdit: () => void;
  onAvatarSelect?: (f: File) => void; uploading?: boolean;
}) {
  return (
    <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
      {/* mobile gradient header strip */}
      <div className="sm:hidden h-16 bg-gradient-to-br from-[#0b1d3a] via-indigo-700 to-sky-600" />

      <div className="px-4 sm:px-8 pb-5 sm:pb-6 pt-0 sm:pt-7">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-5">
          <div className="shrink-0 -mt-10 sm:mt-0">
            <label className="relative inline-block cursor-pointer group size-20 sm:size-24" title="click to upload photo">
              <SafeAvatar
                src={avatar}
                name={displayName}
                email={email}
                className="size-20 sm:size-24 rounded-2xl object-cover ring-4 ring-white sm:ring-1 sm:ring-slate-200 shadow-md sm:shadow-sm"
                fallbackClassName="size-20 sm:size-24 rounded-2xl grid place-items-center bg-gradient-to-br from-sky-500 to-indigo-600 text-white text-2xl font-bold ring-4 ring-white sm:ring-1 sm:ring-slate-200 shadow-md sm:shadow-sm"
                roundedClassName="rounded-2xl"
              >
                {initials}
              </SafeAvatar>
              <span className="absolute inset-0 rounded-2xl bg-black/45 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-[10px] sm:text-xs font-semibold">
                {uploading ? "uploading…" : "change photo"}
              </span>
              <span className="sm:hidden absolute -bottom-1 -right-1 size-6 rounded-full bg-[#0b1d3a] text-white grid place-items-center ring-2 ring-white shadow">
                <Upload className="size-3" />
              </span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; if (f && onAvatarSelect) onAvatarSelect(f); e.currentTarget.value = ""; }}
              />
            </label>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight truncate w-full sm:w-auto">{displayName}</h1>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                <Shield className="size-3" /> verified
              </span>
            </div>
            <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-x-4 sm:gap-y-1 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5 min-w-0"><Mail className="size-3.5 shrink-0" /> <span className="truncate">{email}</span></span>
              {city && <span className="inline-flex items-center gap-1.5"><MapPin className="size-3.5" /> {city}</span>}
              {joinedAt && <span className="inline-flex items-center gap-1.5"><CalendarDays className="size-3.5" /> joined {joinedAt}</span>}
            </div>
            {bio && <p className="mt-2 text-sm text-slate-600 max-w-2xl leading-relaxed">{bio}</p>}
          </div>

          <div className="hidden sm:flex flex-col items-stretch gap-2 shrink-0">
            <Button type="button" onClick={onEdit} size="sm" className="gap-1.5 bg-[#0b1d3a] hover:bg-[#0b1d3a]/90">
              <Pencil className="size-3.5" /> Edit Profile
            </Button>
            <Button asChild type="button" variant="outline" size="sm" className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800">
              <Link to="/refer"><GiftIcon className="size-3.5" /> Refer &amp; Earn</Link>
            </Button>
          </div>
        </div>

        <div className="sm:hidden mt-4 grid grid-cols-2 gap-2">
          <Button type="button" onClick={onEdit} size="sm" className="gap-1.5 bg-[#0b1d3a] hover:bg-[#0b1d3a]/90 h-10">
            <Pencil className="size-3.5" /> Edit Profile
          </Button>
          <Button asChild type="button" variant="outline" size="sm" className="gap-1.5 h-10 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800">
            <Link to="/refer"><GiftIcon className="size-3.5" /> Refer &amp; Earn</Link>
          </Button>
        </div>

      </div>
    </div>
  );
}

/* ============ Overview ============ */
function OverviewTab({
  savedCount, ticketCount, bookingCount, onJump,
}: {
  user: { email?: string | null };
  profile: { avatar_url?: string | null; home_city?: string | null; bio?: string | null } | null | undefined;
  savedCount: number;
  ticketCount: number;
  bookingCount: number;
  savedItems: Item[];
  onJump: (k: TabKey) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
      <StatCard label="Saved" value={savedCount} icon={Bookmark} tint="#e11d48" onClick={() => onJump("saved")} />
      <StatCard label="Tickets" value={ticketCount} icon={Ticket} tint="#f59e0b" onClick={() => onJump("bookings")} />
      <StatCard label="Bookings" value={bookingCount} icon={CalendarDays} tint="#0d6efd" onClick={() => onJump("bookings")} />
    </div>
  );
}

/* ============ Sub-components ============ */
function StatCard({
  label, value, icon: Icon, tint, onClick,
}: {
  label: string; value: number;
  icon: typeof LayoutDashboard; tint: string; onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full min-w-0 text-left rounded-2xl bg-white border border-slate-200 shadow-sm px-3 py-3 sm:px-4 sm:py-3.5 hover:bg-slate-50"
    >
      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
        <span className="size-7 sm:size-8 rounded-lg sm:rounded-xl grid place-items-center shrink-0" style={{ background: `${tint}18`, color: tint }}>
          <Icon className="size-3.5 sm:size-4" />
        </span>
        <span className="min-w-0 flex-1 truncate text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide sm:tracking-wider text-slate-500">{label}</span>
      </div>
      <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl font-bold tabular-nums text-slate-900 leading-none">{value}</div>
    </button>
  );
}


function BookingsTab({
  myBookings,
  bookingsLoading,
  totalTicketCount,
  myOrders,
  ordersLoading,
  onCancel,
}: {
  myBookings: any[];
  bookingsLoading: boolean;
  totalTicketCount: number;
  myOrders: any[];
  ordersLoading: boolean;
  onCancel: (id: string) => Promise<void>;
}) {
  const [view, setView] = useState<"reservations" | "tickets">("reservations");
  const tabs = [
    { key: "reservations" as const, label: "reservations", count: myBookings.length },
    { key: "tickets" as const, label: "tickets", count: totalTicketCount },
  ];
  return (
    <div className="space-y-5">
      <div role="tablist" aria-label="booking view" className="inline-flex w-full sm:w-auto items-center gap-1 p-1 rounded-full bg-slate-100 border border-slate-200">
        {tabs.map((t) => {
          const active = view === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setView(t.key)}
              className={[
                "flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2 rounded-full text-xs font-semibold tracking-tight transition-all",
                active
                  ? "bg-navy text-white shadow-[0_4px_12px_-4px_rgba(15,23,42,0.4)]"
                  : "text-slate-600 hover:text-slate-900",
              ].join(" ")}
            >
              {t.label}
              {t.count > 0 && (
                <span
                  className={[
                    "inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold",
                    active ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700",
                  ].join(" ")}
                >
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {view === "reservations" ? (
        <Card title={`reservations${myBookings.length ? ` · ${myBookings.length}` : ""}`}>
          <BookingsList bookings={myBookings} loading={bookingsLoading} onCancel={onCancel} />
        </Card>
      ) : (
        <Card title={`tickets${totalTicketCount ? ` · ${totalTicketCount}` : ""}`}>
          <OrdersList orders={myOrders} loading={ordersLoading} mode="tickets" />
        </Card>
      )}
    </div>
  );
}


function Card({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-3.5 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {right}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

function Field({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className={wide ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
      <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

function Timeline({ children }: { children: React.ReactNode }) {
  return <ul className="relative">{children}</ul>;
}

function TimelineItem({ icon: Icon, color, title, meta, last }: { icon: typeof LayoutDashboard; color: string; title: string; meta: string; last?: boolean }) {
  return (
    <li className="relative flex gap-3 pb-4 last:pb-0">
      {!last && <span className="absolute left-4 top-8 bottom-0 w-px bg-slate-200" />}
      <div className={`relative z-10 size-8 rounded-full grid place-items-center ${color} ring-4 ring-white`}>
        <Icon className="size-3.5" />
      </div>
      <div className="flex-1 min-w-0 pt-1">
        <p className="text-sm text-slate-800 truncate">{title}</p>
        <p className="text-[11px] text-slate-400 mt-0.5">{meta}</p>
      </div>
    </li>
  );
}

function HealthRow({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-700">{label}</span>
      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ${ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
        {ok ? <Check className="size-3" /> : <X className="size-3" />} {ok ? "done" : "todo"}
      </span>
    </div>
  );
}

function Badge({ icon: Icon, label, color }: { icon: typeof LayoutDashboard; label: string; color: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-1 border ${color}`}>
      <Icon className="size-3" /> {label}
    </span>
  );
}

function QuickLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between rounded-md border border-slate-200 hover:border-sky-300 hover:bg-sky-50 px-3 py-2.5 text-sm text-slate-700 hover:text-sky-900 transition-colors"
    >
      {label} <ChevronRight className="size-3.5" />
    </Link>
  );
}



function Empty({ text, hint, action }: { text: string; hint?: string; action?: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
      <p className="font-medium text-sm text-slate-700">{text}</p>
      {hint && <p className="mt-1.5 text-xs text-slate-500 max-w-sm mx-auto">{hint}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

function ChangePasswordCard({ email }: { email: string }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState({ c: false, n: false, x: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!current || !next || !confirm) { setError("fill all fields"); return; }
    if (next.length < 8) { setError("new password must be at least 8 characters"); return; }
    if (next !== confirm) { setError("new passwords do not match"); return; }
    if (next === current) { setError("new password must be different"); return; }

    setBusy(true);
    try {
      // verify current password
      const { error: verifyErr } = await supabase.auth.signInWithPassword({ email, password: current });
      if (verifyErr) { setError("current password is incorrect"); setBusy(false); return; }

      const { error: updErr } = await supabase.auth.updateUser({ password: next });
      if (updErr) { setError(updErr.message); setBusy(false); return; }

      setCurrent(""); setNext(""); setConfirm("");
      toast.success("password changed successfully");
    } catch (err: any) {
      setError(err?.message ?? "could not change password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="change password">
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <PwField id="cur-pw" label="current password" value={current} onChange={setCurrent} show={show.c} onToggle={() => setShow((s) => ({ ...s, c: !s.c }))} autoComplete="current-password" />
        <PwField id="new-pw" label="new password" value={next} onChange={setNext} show={show.n} onToggle={() => setShow((s) => ({ ...s, n: !s.n }))} autoComplete="new-password" />
        <PwField id="cnf-pw" label="confirm new password" value={confirm} onChange={setConfirm} show={show.x} onToggle={() => setShow((s) => ({ ...s, x: !s.x }))} autoComplete="new-password" />
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <div className="pt-2 border-t border-slate-100 flex justify-end">
          <Button type="submit" disabled={busy} className="gap-1.5 bg-sky-600 hover:bg-sky-700">
            <Lock className="size-4" /> {busy ? "updating…" : "change password"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PwField({ id, label, value, onChange, show, onToggle, autoComplete }: {
  id: string; label: string; value: string; onChange: (v: string) => void;
  show: boolean; onToggle: () => void; autoComplete: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="relative">
        <Input id={id} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} className="pr-10" autoComplete={autoComplete} minLength={8} />
        <button type="button" onClick={onToggle} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" tabIndex={-1} aria-label={show ? "hide" : "show"}>
          {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
    </div>
  );
}
